import { SentryApiClient } from '_generated/api';
import { useApi } from 'App';
import { useAsyncData } from 'shared';
import { useSentryUser } from 'Users/hooks/SentryUserProvider';

export function useComparableSensors(
  sensorDetails?: SentryApiClient.SensorDetailsDTO
) {
  const { user } = useSentryUser();
  const { sensorApi, siteApi } = useApi();

  return useAsyncData(
    () => fetchComparableSensors(sensorDetails, user, sensorApi, siteApi),
    [sensorDetails, user, sensorApi, siteApi]
  );
}

export interface SiteSensorBatch {
  siteName: string;
  sensors: SentryApiClient.PagedResultsDTOOfSensorDetailsAndSensorDetailsDTO;
}

export function isSiteSensorBatchArray(
  data?:
    | SentryApiClient.PagedResultsDTOOfSensorDetailsAndSensorDetailsDTO
    | SentryApiClient.SensorDTO[]
    | SiteSensorBatch[]
): data is SiteSensorBatch[] {
  return (
    Array.isArray(data) &&
    data.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'siteName' in item &&
        'sensors' in item
    )
  );
}

async function fetchComparableSensors(
  sensorDetails: SentryApiClient.SensorDetailsDTO | undefined,
  user: SentryApiClient.UserDTO | undefined,
  sensorApi: SentryApiClient.SensorsClient,
  siteApi: SentryApiClient.SitesClient
) {
  if (
    sensorDetails === undefined ||
    sensorDetails.siteId === undefined ||
    user === undefined
  ) {
    return undefined;
  }

  //Fetch the site so we can figure out what company it belongs to
  const site = await siteApi.getById(sensorDetails.siteId);

  //If the site belongs to a company that the user is a member of, we can fetch the sensors for that company
  if (site.company && user.companies?.includes(site.company)) {
    const sites = await siteApi.get(2000, 1, undefined, undefined, [
      {
        field: SentryApiClient.SiteFilters.CompanyId,
        operator: SentryApiClient.Operator.Equals,
        value: site.company.toString(),
      },
    ]);

    // For each site, fetch only sensors assigned to its panels.
    // This excludes orphaned sensors (never installed) and soft-deleted sensors.
    return (
      await Promise.all(
        sites.items?.map((site) =>
          buildSiteSensorBatch(site, sensorApi, siteApi)
        ) ?? []
      )
    ).filter((r): r is SiteSensorBatch => r !== null);
  }

  // Site does not belong to a company the user is a member of —
  // return only sensors assigned to panels at this specific site.
  return sensorsAssignedToSitePanels(sensorDetails.siteId, sensorApi, siteApi);
}

/**
 * Fetch all sensors assigned to any panel at a site, flattened into a single array.
 * Only sensors with a non-null ParentPanelId are returned, which excludes
 * orphaned sensors and soft-deleted sensors.
 */
async function sensorsAssignedToSitePanels(
  siteId: number,
  sensorApi: SentryApiClient.SensorsClient,
  siteApi: SentryApiClient.SitesClient
): Promise<SentryApiClient.SensorDTO[]> {
  const sensorResults = await sensorApi.get(2000, 1, undefined, undefined, [
    {
      field: SentryApiClient.SensorFilters.SiteId,
      operator: SentryApiClient.Operator.Equals,
      value: siteId.toString(),
    },
    {
      field: SentryApiClient.SensorFilters.ParentPanelId,
      operator: SentryApiClient.Operator.NotEquals,
      value: undefined,
    },
  ]);

  return sensorResults.items ?? [];
}

async function buildSiteSensorBatch(
  site: SentryApiClient.SiteDTO,
  sensorApi: SentryApiClient.SensorsClient,
  siteApi: SentryApiClient.SitesClient
): Promise<SiteSensorBatch | null> {
  if (site.id === undefined || site.id === null) return null;

  const sensors = await sensorsAssignedToSitePanels(
    site.id,
    sensorApi,
    siteApi
  );

  return {
    siteName: site.name,
    sensors: {
      items: sensors,
      totalItems: sensors.length,
      pageSize: sensors.length,
      pageIndex: 1,
      totalPages: 1,
    },
  };
}
